
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Bell, Check, AlertTriangle, Stethoscope, Shield, FileText, Info, Clock } from 'lucide-react';

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);

  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { axiosAuth } = useAuth();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const api = axiosAuth();
      const res = await api.get('/notifications?limit=5');
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (err) { 
      console.error('Failed to fetch notifications:', err); 
    } finally {
      setLoading(false);
    }
  }, [axiosAuth]);

  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 60 seconds
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markAsRead = async (notificationId, e) => {
    e.stopPropagation();
    try {
      const api = axiosAuth();
      await api.post(`/notifications/${notificationId}/read`);
      fetchNotifications();
    } catch (err) { console.error(err); }
  };

  const getIcon = (type) => {
    switch(type) {
      case 'medical_expiry':
      case 'medical_warning':
        return Stethoscope;
      case 'license_expiry':
      case 'license_warning':
        return Shield;
      case 'document_expiry':
        return FileText;
      case 'eligibility_warning':
        return AlertTriangle;
      case 'flight_scheduled':
        return Clock;
      default:
        return Info;
    }
  };

  const getIconColor = (type, severity) => {
    if (severity === 'critical' || type?.includes('expir')) return 'text-red-400';
    if (severity === 'warning' || type?.includes('warning')) return 'text-amber-400';
    return 'text-cyan-400';
  };

  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        data-testid="notification-bell"
        onClick={() => setIsOpen(!isOpen)}
        className="p-2.5 hover:bg-white/[0.04] rounded-xl relative transition-all duration-200 group"
      >
        <Bell className="w-[18px] h-[18px] text-muted-foreground group-hover:text-foreground transition-colors" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-cyan-500 rounded-full text-[10px] font-bold text-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-card border border-border rounded-xl shadow-2xl z-50 animate-fade-in overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <h3 className="text-sm font-semibold">Notifications</h3>
            {unreadCount > 0 && (
              <span className="text-xs text-cyan-400">{unreadCount} unread</span>
            )}
          </div>

          {/* Notification List */}
          <div className="max-h-80 overflow-y-auto">
            {loading && notifications.length === 0 ? (
              <div className="p-4 text-center">
                <div className="w-5 h-5 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-6 text-center">
                <Bell className="w-8 h-8 text-muted-foreground mx-auto mb-2 opacity-50" />
                <p className="text-sm text-muted-foreground">No notifications</p>
              </div>
            ) : (
              notifications.map((notif) => {
                const Icon = getIcon(notif.type);
                const iconColor = getIconColor(notif.type, notif.severity);
                return (
                  <div
                    key={notif.notification_id}
                    className={`px-4 py-3 border-b border-border/50 last:border-b-0 hover:bg-white/[0.02] transition-colors cursor-pointer ${
                      !notif.read ? 'bg-cyan-500/5' : ''
                    }`}
                    onClick={() => {
                      setIsOpen(false);
                      navigate('/notifications');
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        notif.read ? 'bg-secondary/50' : 'bg-cyan-500/10'
                      }`}>
                        <Icon className={`w-4 h-4 ${notif.read ? 'text-muted-foreground' : iconColor}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className={`text-xs font-medium truncate ${notif.read ? 'text-muted-foreground' : 'text-foreground'}`}>
                            {notif.title}
                          </p>
                          {!notif.read && (
                            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground truncate mt-0.5">{notif.message}</p>
                        <p className="text-[10px] text-muted-foreground mt-1">
                          {formatTimeAgo(notif.created_at)}
                        </p>
                      </div>
                      {!notif.read && (
                        <button
                          onClick={(e) => markAsRead(notif.notification_id, e)}
                          className="p-1.5 hover:bg-white/[0.06] rounded-lg text-muted-foreground hover:text-foreground transition-colors"
                          title="Mark as read"
                        >
                          <Check className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2.5 border-t border-border bg-secondary/30">
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/notifications');
              }}
              className="w-full text-center text-xs text-cyan-400 hover:text-cyan-300 font-medium transition-colors"
            >
              View all notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
